import Header from 'components/header/Header';
import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import RMTable, {
  RowExtraInfo,
  RowInfoType,
} from 'components/customComponents/Controls/RMTable';
import './Fragment.scss';
import { RMInput } from '../ProteinFormulation/RMInput';
import FragmentService from 'business/services/FragmentService';
import { FragmentScreenViewDTO, SaveFragmentScreenViewDTO } from 'business/models/Models';
import { IOption } from 'components/input/selectInput';
import { LoadingModal, useModalHandlers } from 'components/modal';
import { cloneDeep, isEmpty } from 'lodash';
import RMTextInput from 'components/customComponents/Controls/RMTextInput';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import {
  getNewId,
  navigationBlockWarningMessage,
  readFileAsBase64String,
  showAlertSnackBar,
  showSnackBar,
} from 'utils/Global';
import { useParams } from 'react-router';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { TreeService } from 'business/services/TreeService';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import RecentDocumentService from 'business/services/RecentDocumentService';
import { KeyboardKeys } from 'constants/types';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

interface EditOptionsType extends InputHTMLAttributes<HTMLInputElement> {
  inputType: React.HTMLInputTypeAttribute;
  options?: IOption[];
}

enum ValidColumns {
  FragmentName = 'Fragment Name',
  Size = 'Size',
  Conc = 'Conc. (mM)',
  Notes = 'Notes',
}
interface IBackup {
  fragmentScreenView: FragmentScreenViewDTO[];
  fragmentNodeName: string;
}

function isCSV(file: File) {
  const nameOk = file.name.toLowerCase().endsWith('.csv');
  const typeOk = file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
  return nameOk || typeOk;
}

export const Fragment = (props) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [addCondition, setAddCondition] = useState<number>(1);
  const [data, setData] = useState<FragmentScreenViewDTO[]>([]);
  const [backupData, setBackupData] = useState<IBackup>(null);
  const [fragmentNodeName, setFragmentNodeName] = useState<string>('');
  const [loadingText, setLoadingText] = useState<string>('Loading');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [isfragmentNodeNameDirty, setIsfragmentNodeNameDirty] = useState<boolean>(false);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [toggleSort, setToggleSort] = useState<boolean>(false);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const [fragmentNodeId, setFragmentNodeId] = useState<number>(-1);
  const [parentNodeId, setParentNodeId] = useState<number>(-1);
  const [fragmentScreenInUseExperiment, setFragmentScreenInUseExperiment] = useState<
    string[]
  >([]);
  const { showModal } = useModalHandlers();
  const showFileNotFoundModal = useFileNotFoundModal();

  const { id: fragmentScreenIdParam } = useParams();
  const fragmentScreenId = parseInt(fragmentScreenIdParam);
  const [accessRightName, setAccessRightName] = useState<string>('');
  const isReadOnly = accessRightName !== ETreeNodeAccessRightNames.Modify;

  // Generic sorting function
  function sortByKey<T>(array: T[], key: string, ascending: boolean): T[] {
    return array.slice().sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];

      if (ascending) {
        if (valueA < valueB) {
          return -1;
        }
        if (valueA > valueB) {
          return 1;
        }
        return 0;
      } else {
        if (valueA < valueB) {
          return 1;
        }
        if (valueA > valueB) {
          return -1;
        }
        return 0;
      }
    });
  }

  const handleTableFilter =
    (filterable: boolean, filterTitle: string, accessor: string) => (event: any) => {
      return (
        <TableSortInputFilter
          event={event}
          title={filterTitle}
          showSearchInput={filterable}
          onSortClick={(e) => {
            setData(sortByKey(data, accessor, toggleSort));
            setToggleSort(!toggleSort);
          }}
        />
      );
    };

  const tableColumns: {
    accessor: string;
    Header: string;
    className?: string;
    editOptions?: EditOptionsType;
    Filter?: (event: any) => JSX.Element;
  }[] = useMemo(() => {
    return [
      {
        accessor: 'conditionNumber',
        Header: 'Cond. Num',
        className: 'nameColumn',
        Filter: handleTableFilter(false, 'Cond. Num', 'conditionNumber'),
      },
      {
        accessor: 'fragmentID',
        Header: 'ID',
        className: 'idColumn',
        Filter: handleTableFilter(false, 'ID', 'fragmentID'),
      },
      {
        accessor: 'fragmentName',
        Header: 'Fragment Name',
        className: 'fragmentNameColumn',
        editOptions: {
          inputType: 'text',
          required: true,
        },
        Filter: handleTableFilter(false, 'Fragment Name', 'fragmentName'),
      },
      {
        accessor: 'size',
        Header: 'Size',
        className: 'sizeColumn',
        editOptions: {
          inputType: 'number',
          min: 0,
          step: 0.1,
        },
        Filter: handleTableFilter(false, 'Size', 'size'),
      },
      {
        accessor: 'workingConcentration',
        Header: 'Conc. (mM)',
        className: 'concentrationColumn',
        editOptions: { inputType: 'number' },
        Filter: handleTableFilter(false, 'Conc. (mM)', 'workingConcentration'),
      },
      {
        accessor: 'notes',
        Header: 'Notes',
        className: 'notesColumn',
        editOptions: {
          inputType: 'text',
          required: true,
        },
        Filter: handleTableFilter(false, 'Notes', 'notes'),
      },
    ];
  }, [data]);

  const onCellChange = (e, index, key) => {
    setData((prevData) => {
      const updatedList = [...prevData]; // Spread the previous data array directly

      const idx = updatedList.findIndex(
        (item) => item.fragmentFragmentScreenId === index,
      );

      if (e.target.type === 'number') {
        updatedList[idx][key] = parseFloat(e.target.value);
      } else {
        updatedList[idx][key] = e.target.value;
      }

      updatedList[idx]['isDirty'] = true;

      return updatedList;
    });
    setSaveDisabled(false);
  };

  const renderTableData = useMemo(() => {
    return [...data].map((fragment) => {
      return {
        conditionNumber: (
          <input
            id="fragment-condition-number-input"
            type="number"
            className="table-input-style"
            onChange={(e) =>
              onCellChange(e, fragment.fragmentFragmentScreenId, 'conditionNumber')
            }
            value={fragment.conditionNumber}
            title={'Condition Number'}
            readOnly={true}
          />
        ),
        fragmentID: (
          <input
            id="fragment-id-input"
            type="number"
            className="table-input-style"
            onChange={(e) =>
              onCellChange(e, fragment.fragmentFragmentScreenId, 'fragmentID')
            }
            value={fragment.fragmentID}
            title={'ID'}
            readOnly={true}
          />
        ),
        fragmentName: (
          <input
            id="fragment-name-input"
            type="text"
            className="table-input-style"
            onChange={(e) =>
              onCellChange(e, fragment.fragmentFragmentScreenId, 'fragmentName')
            }
            value={fragment.fragmentName}
            title={'Fragment Name'}
            readOnly={fragmentScreenInUseExperiment.length > 0 || isReadOnly}
          />
        ),
        size: (
          <input
            id="fragment-size-input"
            type="number"
            className="table-input-style"
            onChange={(e) => onCellChange(e, fragment.fragmentFragmentScreenId, 'size')}
            value={fragment.size}
            title={'Size'}
            readOnly={fragmentScreenInUseExperiment.length > 0 || isReadOnly}
          />
        ),
        workingConcentration: (
          <input
            id="fragment-working-concentration-input"
            type="number"
            className="table-input-style"
            onChange={(e) =>
              onCellChange(e, fragment.fragmentFragmentScreenId, 'workingConcentration')
            }
            value={fragment.workingConcentration}
            title={'Conc. (mM)'}
            readOnly={fragmentScreenInUseExperiment.length > 0 || isReadOnly}
          />
        ),
        notes: (
          <input
            id="fragment-notes-input"
            type="text"
            className="table-input-style flex justify-center"
            onChange={(e) => onCellChange(e, fragment.fragmentFragmentScreenId, 'notes')}
            value={fragment.notes}
            title={'Notes'}
            readOnly={fragmentScreenInUseExperiment.length > 0 || isReadOnly}
          />
        ),
      };
    });
  }, [data, selectedRows]);

  useEffect(() => {
    fetchAndUpdateFragmentScreen();
  }, []);

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (!saveDisabled && !isEmpty(fragmentNodeName)) {
          saveHandlder();
        }
      }
    },
    [saveHandlder, saveDisabled, fragmentNodeName],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  const fetchAndUpdateFragmentScreen = async () => {
    try {
      const fragmentService = new FragmentService();
      const fragmentData = await fragmentService.getFragmentScreen(fragmentScreenId);
      setAccessRightName(fragmentData?.accessRightName);
      setParentNodeId(fragmentData.parentId);
      setFragmentNodeId(fragmentData.fragmentNodeId);
      setFragmentNodeName(fragmentData.fragmentNodeName);
      document.title = fragmentData.fragmentNodeName;
      setBackupData({
        fragmentScreenView: cloneDeep(fragmentData.fragmentScreenView),
        fragmentNodeName: fragmentData.fragmentNodeName,
      });
      if (!fragmentData.fragmentScreenView) {
        return null;
      } else {
        setData(fragmentData.fragmentScreenView);
      }
      setFragmentScreenInUseExperiment(fragmentData.fragmentScreenInUseExperiments);
      const recentDocumentService = new RecentDocumentService();
      await recentDocumentService.saveDocumentToRecents(fragmentData.fragmentNodeId);
    } catch (error) {
      if (error.status === 404) {
        showFileNotFoundModal();
      } else {
        throw error;
      }
    }
  };

  async function saveHandlder() {
    try {
      if (Object.keys(rowExtraInfo).length > 0) {
        showSnackBar('Please correct error(s) highlighted then press save');
        return;
      }

      setLoadingText('Saving');
      setIsLoading(true);
      const service = new FragmentService();
      const saveFragmentScreenViewDTO = new SaveFragmentScreenViewDTO();
      saveFragmentScreenViewDTO.tasksList = data;
      saveFragmentScreenViewDTO.deletedActionsIds = deletedIds;
      saveFragmentScreenViewDTO.parentNodeId = parentNodeId;
      saveFragmentScreenViewDTO.fragmentNodeName = fragmentNodeName;
      saveFragmentScreenViewDTO.isNameDirty = isfragmentNodeNameDirty;
      saveFragmentScreenViewDTO.fragmentNodeId = fragmentScreenId;
      const fragmentScreenResult = await service.SaveFragmentScreenView(
        saveFragmentScreenViewDTO,
      );

      if (fragmentScreenResult.fragmentResponseDTO.isFragmentScreenInUse) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          `'${fragmentNodeName}' is already in use. Please unlink this Fragment from Experiment first.`,
        );
        setIsLoading(false);
        discardHandler();
        return;
      }

      if (fragmentScreenResult.fragmentResponseDTO.isNameAlreadyExists) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          `The name '${fragmentNodeName}' is already in use. Please choose another name.`,
        );
        setIsLoading(false);
        return;
      }

      setBackupData({
        fragmentScreenView: cloneDeep(fragmentScreenResult.tasksList),
        fragmentNodeName: fragmentNodeName,
      });
      setDeletedIds([]);
      setData(fragmentScreenResult.tasksList);
      setIsLoading(false);
      setSaveDisabled(true);
      showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Saved Successfully');
      //Broadcast to other tabs that node is updated
      if (isfragmentNodeNameDirty) {
        const treeService = new TreeService();
        const treeNodeRequest = treeService.getTreeNodeById(fragmentNodeId);
        treeNodeRequest.then((node) => {
          explorerBroadcastChannel.postMessage({
            action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
            payload: node,
          });
        });
        setIsfragmentNodeNameDirty(false);
      }
    } catch (err) {
      setIsLoading(false);
      if (err.StatusCode === 404) {
        showFileNotFoundModal();
      } else {
        showSnackBar('Error while Saving Protein Formulation');
      }
    }
  }

  function discardHandler() {
    setData(cloneDeep(backupData.fragmentScreenView) ?? []);
    setFragmentNodeName(backupData.fragmentNodeName);
    document.title = backupData.fragmentNodeName;
    setIsfragmentNodeNameDirty(false);
    setDeletedIds([]);
    setSaveDisabled(true);
  }

  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: !saveDisabled && !isEmpty(fragmentNodeName),
    onSave: saveHandlder,
    onDiscard: discardHandler,
    navigationBlockCallback: () => {
      if (!saveDisabled) {
        return navigationBlockWarningMessage;
      }
    },
  });

  const inUseFragmentWarningModal = (heading: string, belowText: string) => {
    showModal({
      heading: heading,
      body: (
        <div id="fragment-inusefragmentwarningmodal-wrapper" className="text-sm">
          <div
            id="fragment-inusefragmentwarningmodal-cancel-container"
            className="bg-red-200 text-red-700 rounded-md px-3 py-1 text-sm"
          >
            <RMSvgIcon
              id="fragment-inusefragmentwarningmodal-cancel-icon"
              name="Cancel"
            ></RMSvgIcon>{' '}
            Fragment condition has been used by active Experiments
            <br />
            {
              <ul id="fragment-inusefragmentwarningmodal-experiment-list">
                {fragmentScreenInUseExperiment.map((item, index) => (
                  <li
                    id={`fragment-inusefragmentwarningmodal-experiment-item-${index}`}
                    key={index}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            }
          </div>
          {belowText}
        </div>
      ),
      scrollable: false,
      isCancelOnly: true,
      noButtonText: 'Close',
    });
  };

  const inValidImportWarningModal = () => {
    showModal({
      heading: 'Import Fragment Screen',
      body: (
        <div id="fragment-invalidimportwarningmodal-wrapper" className="text-sm">
          <div
            id="fragment-invalidimportwarningmodal-cancel-container"
            className="bg-red-200 text-red-700 rounded-md px-3 py-1 text-sm"
          >
            <RMSvgIcon
              id="fragment-invalidimportwarningmodal-cancel-icon"
              name="Cancel"
            ></RMSvgIcon>{' '}
            Unable to import. You can only import a CSV file with correct column order.
            Download and use the provided template to prevent any errors.
          </div>
        </div>
      ),
      scrollable: false,
      isCancelOnly: true,
      noButtonText: 'OK',
    });
  };

  const onClickAdd = (e) => {
    if (fragmentScreenInUseExperiment.length > 0) {
      inUseFragmentWarningModal(
        'Add Fragment Screen',
        'Please unlink this fragment from Experiment to add new condition.',
      );
    } else {
      let maxConditionNumber = data.reduce((max, item) => {
        return item.conditionNumber > max ? item.conditionNumber : max;
      }, 0);

      const newFragmentScreenViewRowArray: FragmentScreenViewDTO[] = [];

      for (let i = 0; i < addCondition; i++) {
        maxConditionNumber++;

        const newFragmentScreenViewRow = new FragmentScreenViewDTO({
          conditionNumber: maxConditionNumber,
          fragmentName: '',
          fragmentID: 0,
          workingConcentration: 0,
          size: 0,
          notes: '',
          fragmentFragmentScreenId: getNewId(),
          fragmentScreenId: fragmentScreenId,
          isDirty: false,
        });
        newFragmentScreenViewRowArray.push(newFragmentScreenViewRow);
      }

      const newArray: FragmentScreenViewDTO[] = [
        ...data,
        ...newFragmentScreenViewRowArray,
      ];
      setData(newArray);
      setSaveDisabled(false);
    }
  };

  const onImportAdd = (importData: any[]) => {
    if (fragmentScreenInUseExperiment.length > 0) {
      inUseFragmentWarningModal(
        'Import Fragment Screen',
        'Please unlink this fragment from Experiment to import.',
      );
    } else {
      // Raw data has at least 2 elements.
      // The first element is header, the second is the first row
      if (importData.length <= 1) {
        inValidImportWarningModal();
        setSaveDisabled(false);
        return;
      }
      let maxConditionNumber = data.reduce((max, item) => {
        return item.conditionNumber > max ? item.conditionNumber : max;
      }, 0);

      const newFragmentScreenViewRowArray: FragmentScreenViewDTO[] = [];

      for (let i = 0; i < importData.length - 1; i++) {
        maxConditionNumber++;

        const newFragmentScreenViewRow = new FragmentScreenViewDTO({
          conditionNumber: maxConditionNumber,
          fragmentName: importData[i][ValidColumns.FragmentName],
          fragmentID: 0,
          workingConcentration: isNaN(importData[i][ValidColumns.Conc])
            ? 0
            : importData[i][ValidColumns.Conc],
          size: isNaN(importData[i][ValidColumns.Size])
            ? 0
            : importData[i][ValidColumns.Size],
          notes: importData[i][ValidColumns.Notes],
          fragmentFragmentScreenId: getNewId(),
          fragmentScreenId: fragmentScreenId,
          isDirty: false,
        });

        if (
          !newFragmentScreenViewRow.fragmentName ||
          newFragmentScreenViewRow.size < 1 ||
          isNaN(newFragmentScreenViewRow.size) ||
          isNaN(newFragmentScreenViewRow.workingConcentration)
        ) {
          inValidImportWarningModal();
          setSaveDisabled(false);
          return;
        }

        newFragmentScreenViewRowArray.push(newFragmentScreenViewRow);
      }

      const newArray: FragmentScreenViewDTO[] = [
        ...data,
        ...newFragmentScreenViewRowArray,
      ];
      setData(newArray);
      setSaveDisabled(false);
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'File imported successfully',
      );
    }
  };

  const onClickDelete = (e) => {
    if (fragmentScreenInUseExperiment.length > 0) {
      inUseFragmentWarningModal(
        'Delete Fragment Screen',
        'Please unlink this fragment from Experiment to delete.',
      );
    } else {
      setDeletedIds(selectedRows.map((index) => data[index]?.fragmentFragmentScreenId));
      // Filter out the selected rows and update their condition numbers
      const filteredData = data.filter((item, index) => !selectedRows.includes(index));
      const updatedData = [];

      // Iterate through the filtered data and update condition numbers
      for (let i = 0; i < filteredData.length; i++) {
        // Increment condition number if the row's condition number is greater than the index
        if (filteredData[i].conditionNumber > i + 1) {
          filteredData[i].conditionNumber = i + 1;
          filteredData[i].isDirty = true;
        }
        updatedData.push(filteredData[i]);
      }

      // Set the updated data
      setData(updatedData);
      setSaveDisabled(false);
      setSelectedRows([]);
    }
  };

  const OnChangeNodeName = (e) => {
    const newValue = e.target.value;
    setFragmentNodeName(newValue);
    document.title = newValue;

    const trimmedNewValue = newValue.trim();
    const trimmedBackupName = (backupData.fragmentNodeName || '').trim();

    if (trimmedNewValue !== trimmedBackupName) {
      setIsfragmentNodeNameDirty(true);
    } else {
      setIsfragmentNodeNameDirty(false);
    }
    setSaveDisabled(false);
  };

  const handleOnRowCLick = (row) => {
    setSelectedRows([row.index]);
  };

  const handleSelectionChange = (rowIndices) => {
    setSelectedRows(rowIndices);
  };

  async function attachmentChangedHandler(e) {
    const file = e.target.files[0];
    if (!isCSV(file)) {
      return;
    }
    await readFileAsBase64String(file, CreateObjectDataCsv);
    e.target.value = '';
  }

  async function CreateObjectDataCsv(base64String: string) {
    // Convert Base64 to a regular string
    const csvString = atob(base64String);
    // Parse CSV data
    const rows = csvString.split('\n').map((row) => row.split(','));

    //Valid columns
    const validColumns: ValidColumns[] = [
      ValidColumns.FragmentName,
      ValidColumns.Size,
      ValidColumns.Conc,
      ValidColumns.Notes,
    ];

    // Extract column names from the first row
    const columns = rows[0];
    // Initialize an array to store objects
    const objectsArray = [];
    // Iterate over the remaining rows to create objects
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const obj = {};

      // Iterate over columns and assign values to object keys
      for (let j = 0; j < columns.length; j++) {
        //To exclude invalid data
        if (validColumns.includes(columns[j] as ValidColumns)) {
          obj[columns[j]] = row[j] || null;
        } // Use null for empty values
      }

      // Add the object to the array
      objectsArray.push(obj);
    }

    onImportAdd(objectsArray);
  }

  const rowExtraInfo: RowExtraInfo = {};

  data.forEach((frag, i) => {
    let msg = '';
    if (!frag.fragmentName) {
      msg = 'Fragment Name cannot be empty.\n';
    } else if (frag.size < 1 || isNaN(frag.size)) {
      msg = 'Fragment Size cannot be 0 or negative.\n';
    } else if (isNaN(frag.workingConcentration)) {
      msg = `${ValidColumns.Conc} cannot be empty.\n`;
    } else if (frag.workingConcentration <= 0) {
      msg = `${ValidColumns.Conc} cannot be 0 or negative.\n`;
    }
    if (msg) {
      rowExtraInfo[i] = { msg, type: RowInfoType.Error };
    }
  });

  return (
    <>
      <Header
        title={fragmentNodeName}
        isNewTheme={true}
        headerButtons={[
          <div id="fragment-wrapper" className="m-2" key={`fragNod`}>
            <a
              id="fragment-header-template-link"
              href={isReadOnly ? undefined : '/assets/import files/Fragment Screen.csv'}
              className={`flex flex-column justify-center items-center text-black ${
                isReadOnly ? 'opacity-50' : 'cursor-pointer'
              }`}
              download={isReadOnly ? undefined : 'Fragment Screen.csv'}
              onClick={(e) => {
                if (isReadOnly) {
                  e.preventDefault();
                }
              }}
            >
              <RMSvgIcon
                id="fragment-header-template-icon"
                name="FragmentDownloadTemplate"
                height={27}
                title="Template"
                width={27}
                fill="#313131"
              ></RMSvgIcon>
              <label id="fragment-header-template-label">Template</label>
            </a>
          </div>,
          <div id="fragment-header-import-wrapper" className="m-2" key={`fragNod1`}>
            <label
              id="fragment-header-import-label"
              className={`flex flex-column justify-center items-center ${
                isReadOnly ? 'opacity-50' : 'cursor-pointer'
              }`}
            >
              <input
                id="fragment-header-import-input"
                type="file"
                accept={'.csv'}
                style={{ margin: '0px 10px', display: 'none' }}
                onInput={!isReadOnly ? attachmentChangedHandler : undefined}
                disabled={isReadOnly}
              />
              <RMSvgIcon
                id="fragment-header-import-icon"
                name="Import"
                height={25}
                title="Template"
                width={25}
              ></RMSvgIcon>
              Import
            </label>
          </div>,
        ]}
        extendedActionSection={saveDiscardButtons}
      />
      <div id="fragment-main-container" className="flex flex-row m-2">
        <div id="fragment-name-section" className="w-[70%] p-1">
          Fragment Screen Name
          <RMTextInput
            style={{ width: '80%', borderRadius: '1px !important', margin: '2px' }}
            onChange={OnChangeNodeName}
            value={fragmentNodeName}
            placeHolder={'Fragment Screen'}
            readOnly={isReadOnly}
          />
        </div>
        <div id="fragment-add-condition-section" className="w-[20%] p-1">
          Add Condition
          <RMInput
            key={'name'}
            className="inputFieldDetails w-[60%]"
            onChange={(e) => {
              setAddCondition(e.target.value);
            }}
            value={addCondition}
            allowDecimals={false}
            readonly={isReadOnly}
          />
        </div>
        <div id="fragment-button-wrapper" className="flex flex-row w-[10%] p-1">
          <RMCustomButton
            isDisabled={isReadOnly}
            iconProps={{
              name: 'Add',
              width: 30,
              height: 30,
              fill: '#474747',
              subfolder: 'Fragment',
            }}
            contentContainerClass="mx-2"
            onClick={onClickAdd}
            titleTip="Add"
          />
          <RMCustomButton
            isDisabled={selectedRows.length < 1 || isReadOnly}
            contentContainerClass="mx-2"
            iconProps={{ name: 'Delete', width: 30, height: 30, subfolder: 'Fragment' }}
            onClick={onClickDelete}
            titleTip="Delete"
          />
        </div>
      </div>
      <div
        id="fragment-table-wrapper"
        style={{ overflow: 'auto', maxHeight: '80%', margin: '10px', width: '99%' }}
      >
        <RMTable
          onCheckboxSelectionChange={handleSelectionChange}
          onRowClicked={handleOnRowCLick}
          tableClass={'w-full'}
          columns={tableColumns}
          data={renderTableData || []}
          selectedIds={selectedRows}
          rowExtraInfo={rowExtraInfo}
          enableMultiSelection={true}
        />
      </div>
      <LoadingModal showModal={isLoading} text={loadingText} />
    </>
  );
};
